'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

export default function AuthPageContent() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] flex-col justify-between p-12 relative overflow-hidden border-r border-border">
        {/* Background texture */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(110,231,183,0.08) 0%, transparent 60%), radial-gradient(circle at 80% 80%, rgba(59,130,246,0.06) 0%, transparent 50%)',
            }}
          />
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <AppLogo size={36} />
          <span className="text-[18px] font-semibold text-foreground tracking-tight">Qelotio</span>
        </div>

        {/* Main content */}
        <div className="relative space-y-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-green" />
              <span className="text-[12px] font-medium text-primary">Policy Enforcement Platform</span>
            </div>
            <h1 className="text-[36px] font-bold text-foreground leading-tight mb-4">
              Turn Policy Into<br />Enforcement.
            </h1>
            <p className="text-[15px] text-muted-foreground leading-relaxed max-w-md">
              Qelotio evaluates important business actions against organizational policy before they execute. People, applications, workflows, automation, and AI agents all governed by the same policy layer.
            </p>
          </div>

          {/* Architecture flow */}
          <div className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">How it works</p>
            {[
              { icon: 'UsersIcon', label: 'Action Request', sub: 'Human, application, workflow, automation, or AI agent', color: 'text-accent' },
              { icon: 'ArrowDownIcon', label: '', sub: '', color: 'text-muted-foreground' },
              { icon: 'ShieldCheckIcon', label: 'Qelotio Policy Enforcement', sub: 'Policy evaluation · Risk controls · Human approvals', color: 'text-primary' },
              { icon: 'ArrowDownIcon', label: '', sub: '', color: 'text-muted-foreground' },
              { icon: 'BuildingOffice2Icon', label: 'Business System', sub: 'Only approved actions execute', color: 'text-purple-400' },
            ].map((item, i) => {
              if (item.label === '') {
                return (
                  <div key={`arch-arrow-${i}`} className="flex items-center pl-4">
                    <Icon name="ArrowDownIcon" size={14} className="text-muted-foreground/40" />
                  </div>
                );
              }
              return (
                <div key={`arch-item-${i}`} className="flex items-center gap-3 bg-muted/30 border border-border rounded-lg px-4 py-3">
                  <div className={`w-8 h-8 rounded-lg bg-card flex items-center justify-center flex-shrink-0 border border-border`}>
                    <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} className={item.color} />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-foreground">{item.label}</p>
                    {item.sub && <p className="text-[11px] text-muted-foreground">{item.sub}</p>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Social proof */}
          <div className="flex items-center gap-6">
            {[
              { value: '€2.84M', label: 'Value governed' },
              { value: '1,847', label: 'Actions evaluated' },
              { value: '97.2%', label: 'Execution success' },
            ].map((stat) => (
              <div key={`proof-${stat.label}`}>
                <p className="text-[18px] font-bold font-tabular text-foreground">{stat.value}</p>
                <p className="text-[11px] text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-[11px] text-muted-foreground">
          © 2026 Qelotio Ltd · Trusted by EU/UK fintechs · SOC 2 Type II
        </p>
      </div>

      {/* Right panel — auth form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={32} />
          <span className="text-[17px] font-semibold text-foreground">Qelotio</span>
        </div>

        <div className="w-full max-w-[420px]">
          {/* Tab switcher */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setTab('login')}
              suppressHydrationWarning
              className={`flex-1 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                tab === 'login' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setTab('signup')}
              suppressHydrationWarning
              className={`flex-1 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                tab === 'signup' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
          </div>

          {tab === 'login' ? (
            <LoginForm onSwitchToSignUp={() => setTab('signup')} />
          ) : (
            <SignUpForm onSwitchToLogin={() => setTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}